"use client";

/**
 * SyncInquiryDialog — a film/ad maker sends a detailed licence inquiry to the
 * track's maker WITHOUT needing an account (so BB doesn't lose the job). Posts
 * to /api/sync/inquiry, which stores it, notifies the maker, and emails the
 * maker + breakinbreadswe@gmail.com. Reserving/paying still requires signup.
 */

import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { SYNC_USAGE_TYPES } from "@/data/sync-usage-types";
import { Loader2, Send } from "lucide-react";

export function SyncInquiryDialog({
  open,
  onOpenChange,
  track,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  track: { id?: string; title?: string; artistName?: string; makerUid?: string };
}) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    requesterName: "",
    requesterEmail: "",
    usageType: SYNC_USAGE_TYPES[0] as string,
    useDescription: "",
    durationMin: "",
    durationSec: "",
    estimatedReleaseDate: "",
    licencePeriodYears: "1",
    exclusive: false,
    specialRequests: "",
  });
  const set = (patch: Partial<typeof f>) => setF((p) => ({ ...p, ...patch }));

  const submit = async () => {
    if (!/.+@.+\..+/.test(f.requesterEmail.trim())) {
      toast({ title: "Add a valid email", description: "So the artist can reply to you.", variant: "destructive" });
      return;
    }
    if (!f.useDescription.trim()) {
      toast({ title: "Describe the use", description: "What is it for, and where will it run?", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch("/api/sync/inquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          trackId: track.id,
          trackTitle: track.title,
          artistName: track.artistName,
          makerUid: track.makerUid,
          ...f,
          durationMin: Number(f.durationMin) || 0,
          durationSec: Number(f.durationSec) || 0,
          licencePeriodYears: Number(f.licencePeriodYears) || 1,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send");
      toast({ title: "Inquiry sent", description: "The artist has been notified and will reply to your email." });
      onOpenChange(false);
    } catch (e: any) {
      toast({ title: "Couldn't send", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Request a licence</DialogTitle>
          <DialogDescription>
            Send the artist the details of your project. No account needed to ask — you only sign up to reserve.
            {track.title ? <> Track: <strong>{track.title}</strong>{track.artistName ? ` — ${track.artistName}` : ""}.</> : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Your name</Label>
              <Input value={f.requesterName} onChange={(e) => set({ requesterName: e.target.value })} placeholder="Name / company" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Email *</Label>
              <Input type="email" value={f.requesterEmail} onChange={(e) => set({ requesterEmail: e.target.value })} placeholder="you@studio.com" />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Project type</Label>
            <select
              value={f.usageType}
              onChange={(e) => set({ usageType: e.target.value })}
              className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {SYNC_USAGE_TYPES.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">What is it for, and where will it run? *</Label>
            <Textarea value={f.useDescription} onChange={(e) => set({ useDescription: e.target.value })} rows={3} placeholder="e.g. 30s TV spot for a sneaker brand, airing in Sweden + online." />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Mins used</Label>
              <Input type="number" min={0} value={f.durationMin} onChange={(e) => set({ durationMin: e.target.value })} placeholder="0" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Secs</Label>
              <Input type="number" min={0} max={59} value={f.durationSec} onChange={(e) => set({ durationSec: e.target.value })} placeholder="30" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Licence years</Label>
              <Input type="number" min={1} value={f.licencePeriodYears} onChange={(e) => set({ licencePeriodYears: e.target.value })} placeholder="1" />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Estimated release date</Label>
            <Input type="date" value={f.estimatedReleaseDate} onChange={(e) => set({ estimatedReleaseDate: e.target.value })} />
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={f.exclusive} onChange={(e) => set({ exclusive: e.target.checked })} className="h-4 w-4 accent-primary" />
            <span>Requesting an exclusive licence</span>
          </label>

          <div className="space-y-1">
            <Label className="text-xs">Special requests (optional)</Label>
            <Textarea value={f.specialRequests} onChange={(e) => set({ specialRequests: e.target.value })} rows={2} placeholder="Edits, stems, territory, deadline…" />
          </div>

          <p className="text-[11px] text-muted-foreground">
            Heads up: commercial licences usually run 1 year at a time; each following year is about +20% of the first-year fee. The artist confirms the final terms.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send inquiry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
